import React, { useEffect, useState, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { useNavRefresh } from '../../context/NavContext';
import API from '../../utils/api';
import styles from './AdminResults.module.css';
import AdminPagination from './AdminPagination';

const getRecoClass = r => ({ advance: styles.tagGreen, hold: styles.tagOrange, reject: styles.tagRed }[r] || '');
const getRecoLabel = r => ({ advance: 'Advance', hold: 'Hold', reject: 'Do Not Advance' }[r] || r);
const getGradeColor = g => ({ A: '#1a5c38', B: '#27ae60', C: '#e67e22', D: '#e67e22', F: '#c0392b' }[g] || '#6b6b6b');

const ScoreCellMemo = memo(function ScoreCell({ score, max, pct, grade }) {
  const color = grade === 'A' ? '#1a5c38' : grade === 'B' ? '#27ae60' : grade === 'C' ? '#e67e22' : grade === 'D' ? '#e67e22' : '#c0392b';
  return (
    <div>
      <span style={{ fontWeight: 500 }}>{score}</span>
      <span style={{ fontSize: '0.7rem', color: '#6b6b6b' }}>/{max}</span>
      <span style={{ fontSize: '0.72rem', color, marginLeft: 4 }}>{grade}</span>
    </div>
  );
});

const ResultRowMemo = memo(function ResultRow({ a, serialNumber, navigate, styles }) {
  const getSection = key => a.sections?.find(s => s.section === key);
  const cog = useMemo(() => getSection('cognitive'), [a.sections]);
  const per = useMemo(() => getSection('personality'), [a.sections]);
  const lea = useMemo(() => getSection('leadership'), [a.sections]);
  const cas = useMemo(() => getSection('casestudy'), [a.sections]);
  const candidateLink = a.candidate?.slug || a.candidate?._id || a._id;
  
  return (
    <tr onClick={() => navigate(`/admin/results/${candidateLink}`)} className={styles.clickRow}>
      <td>{serialNumber}</td>
      <td>
        <div className={styles.candidateCell}>
          <div>
            <div className={styles.name}>{a.candidate?.fullName}</div>
            <div className={styles.email}>{a.candidate?.email}</div>
          </div>
        </div>
      </td>
      <td><span className={styles.roleTag}>{a.candidate?.role}</span></td>
      <td>{cog ? <ScoreCellMemo score={cog.rawScore} max={100} pct={cog.percentage} grade={cog.grade} /> : <span className={styles.na}>—</span>}</td>
      <td>{per ? <ScoreCellMemo score={per.rawScore} max={100} pct={per.percentage} grade={per.grade} /> : <span className={styles.na}>—</span>}</td>
      <td>{lea ? <ScoreCellMemo score={lea.rawScore} max={100} pct={lea.percentage} grade={lea.grade} /> : <span className={styles.na}>—</span>}</td>
      <td>{cas ? <ScoreCellMemo score={cas.rawScore} max={100} pct={cas.percentage} grade={cas.grade} /> : <span className={styles.na}>—</span>}</td>
      <td><span style={{ fontWeight: 600 }}>{a.totalScore}</span><span className={styles.muted}>/400</span></td>
      <td><span style={{ fontWeight: 700, color: getGradeColor(a.overallGrade) }}>{a.overallGrade}</span></td>
      <td><span className={`${styles.recoTag} ${getRecoClass(a.recommendation)}`}>{getRecoLabel(a.recommendation)}</span></td>
      <td className={styles.muted}>{new Date(a.submittedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
      <td><span className={styles.viewLink}>View →</span></td>
    </tr>
  );
});

export default function AdminResults() {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [recommendationCounts, setRecommendationCounts] = useState({ all: 0, advance: 0, hold: 0, reject: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState(null);
  const { refreshKey } = useNavRefresh();
  const navigate = useNavigate();

  // Filter assessments based on search term
  const filteredAssessments = assessments.filter(a => 
    (a.candidate?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     a.candidate?.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  useEffect(() => {
    setLoading(true);
    const recommendation = filter === 'all' ? '' : `&recommendation=${filter}`;
    API.get(`/admin/assessments?page=${page}&limit=${pageSize}${recommendation}`)
      .then(res => {
        setAssessments(res.data.data || res.data);
        setPagination(res.data.pagination);
        if (res.data.counts) {
          setRecommendationCounts(res.data.counts);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [refreshKey, page, pageSize, filter]);

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <h2 className={styles.pageTitle}>Assessment Results</h2>
            <p className={styles.pageSub}>{pagination?.total || 0} completed assessment{pagination?.total !== 1 ? 's' : ''}</p>
          </div>
          <div className={styles.filters}>
            {[
              { key: 'all', label: 'All', count: recommendationCounts.all },
              { key: 'advance', label: 'Advance', count: recommendationCounts.advance },
              { key: 'hold', label: 'Hold', count: recommendationCounts.hold },
              { key: 'reject', label: 'Do Not Advance', count: recommendationCounts.reject }
            ].map(f => (
              <button key={f.key} className={`${styles.filterBtn} ${filter === f.key ? styles.active : ''}`} onClick={() => { setFilter(f.key); setPage(1); }}>
                {f.label} <span className={styles.filterCount}>({f.count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Search Bar */}
        <div className={styles.searchBar}>
          <input 
            type="text" 
            className={styles.searchInput}
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          />
        </div>

        <div className={styles.tableCard}>
          {loading ? (
            <div className={styles.empty}>Loading results...</div>
          ) : filteredAssessments.length === 0 ? (
            <div className={styles.empty}>{searchTerm ? 'No results match your search.' : 'No results found.'}</div>
          ) : (
            <>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Candidate</th>
                      <th>Role</th>
                      <th>Cognitive</th>
                      <th>Personality</th>
                      <th>Leadership</th>
                      <th>Case Study</th>
                      <th>Total</th>
                      <th>Grade</th>
                      <th>Recommendation</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssessments.map((a, index) => (
                      <ResultRowMemo
                        key={a._id}
                        a={a}
                        serialNumber={(page - 1) * pageSize + index + 1}
                        navigate={navigate}
                        styles={styles}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {pagination && (
                <AdminPagination
                  page={pagination.page}
                  pageSize={pageSize}
                  total={pagination.total}
                  onPageChange={setPage}
                  onPageSizeChange={size => { setPageSize(size); setPage(1); }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
